import {avatarColorPalette} from '@dropbox/dig-components/avatar';
import {Text} from '@dropbox/dig-components/typography';
import {Atoms, Box} from '@dropbox/dig-foundations';
import {$generateNodesFromDOM} from '@lexical/html';
import {$convertFromMarkdownString, type Transformer} from '@lexical/markdown';
import {AutoFocusPlugin} from '@lexical/react/LexicalAutoFocusPlugin';
import {CheckListPlugin} from '@lexical/react/LexicalCheckListPlugin';
import {LexicalCollaboration} from '@lexical/react/LexicalCollaborationContext';
import {LexicalComposer} from '@lexical/react/LexicalComposer';
import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {ContentEditable} from '@lexical/react/LexicalContentEditable';
import {LexicalErrorBoundary} from '@lexical/react/LexicalErrorBoundary';
import {HistoryPlugin} from '@lexical/react/LexicalHistoryPlugin';
import {ListPlugin} from '@lexical/react/LexicalListPlugin';
import {MarkdownShortcutPlugin} from '@lexical/react/LexicalMarkdownShortcutPlugin';
import {OnChangePlugin} from '@lexical/react/LexicalOnChangePlugin';
import {RichTextPlugin} from '@lexical/react/LexicalRichTextPlugin';
import {TabIndentationPlugin} from '@lexical/react/LexicalTabIndentationPlugin';
import {TablePlugin} from '@lexical/react/LexicalTablePlugin';
import * as Sentry from '@sentry/react';
import {employeeAtom} from 'atoms/employee';
import cx from 'classnames';
import {EmployeeLdap} from 'client';
import {PageSpinner} from 'components/shared/PageSpinner';
import {sanitizeCommentContent} from 'helpers/commentContent';
import {isProd} from 'helpers/environment';
import {useCollabJWTToken} from 'hooks/useJWTToken';
import {useLineClampOverflowMeasure} from 'hooks/useLineClampOverflowMeasure';
import {useAtomValue} from 'jotai';
import {
  $createParagraphNode,
  $createTextNode,
  $getRoot,
  $isDecoratorNode,
  $isElementNode,
  $selectAll,
  EditorState,
  LexicalEditor,
} from 'lexical';
import aiSuggestionStyles from 'plugins/aiRules/AISuggestions.module.css';
import {SCOPED_HTML_HEADING_IMPORT_ATTR} from 'plugins/enhancedHeadings/EnhancedHeadingNode';
import {
  EnhancedHeadingPlugin,
  EnhancedHeadingType,
} from 'plugins/enhancedHeadings/EnhancedHeadingPlugin';
import {RoleProvider, useIsTemplateMode} from 'plugins/enhancedHeadings/RoleContext';
import {EnhancedTableCellPlugin} from 'plugins/enhancedTableCells/EnhancedTableCellPlugin';
import {NormalizeTableCellsPlugin} from 'plugins/enhancedTableCells/NormalizeTableCellsPlugin';
import {
  forwardRef,
  ReactNode,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import {nameToInitials} from 'utilities';

import {CollabProvider} from './CollabProvider';
import styles from './editor/Editor.module.css';
import {editorNodes} from './editor/editorNodes';
import {LinkNodePlugin} from './editor/nodes/LinkNodePlugin';
import {PlanningTableReserveProvider} from './editor/PlanningTableReserveContext';
import {AuraVideoPlugin} from './editor/plugins/AuraVideoPlugin';
import {AutoEmbedPlugin} from './editor/plugins/AutoEmbedPlugin';
import {AutoLinkCompanyOSPlugin} from './editor/plugins/AutoLinkCompanyOSPlugin';
import {AutoLinkPlugin} from './editor/plugins/AutoLinkPlugin';
import {CapturePlugin} from './editor/plugins/CapturePlugin';
import {editorCollabExcludedProperties} from './editor/plugins/collab/collabExcludedProperties';
import {CollaborationPlugin} from './editor/plugins/collab/CollaborationPlugin';
import {DisableTableSelectionPlugin} from './editor/plugins/DisableTableSelectionPlugin';
import {DisallowImagesPlugin} from './editor/plugins/DisallowImagesPlugin';
import {
  DocumentExportPlugin,
  type DocumentExportPreset,
} from './editor/plugins/DocumentExportPlugin';
import {DragDropPastePlugin} from './editor/plugins/DragDropPastePlugin';
import {DynamicNodeDeletionGuardPlugin} from './editor/plugins/DynamicNodeDeletionGuardPlugin';
import {EllipsisReplacementPlugin} from './editor/plugins/EllipsisReplacementPlugin';
import {EmojiPickerPlugin} from './editor/plugins/EmojiPickerPlugin';
import {EmptyLinePastePlugin} from './editor/plugins/EmptyLinePastePlugin';
import {FloatingLinkEditorPlugin} from './editor/plugins/FloatingLinkEditor';
import {FloatingRedactionEditorPlugin} from './editor/plugins/FloatingRedactionEditor';
import {FormKeyboardNavigationPlugin} from './editor/plugins/FormKeyboardNavigationPlugin';
import {ImageLightboxPlugin} from './editor/plugins/ImageLightboxPlugin';
import {ImagesPlugin} from './editor/plugins/ImagesPlugin';
import {KalturaPlugin} from './editor/plugins/KalturaPlugin';
import {KeystrokeCountPlugin} from './editor/plugins/KeystrokeCountPlugin';
import {LexicalClickableLinkPlugin} from './editor/plugins/LexicalClickableLinkPlugin';
import {LinkPlugin} from './editor/plugins/LinkPlugin';
import {ListBackspacePlugin} from './editor/plugins/ListBackspacePlugin';
import {ListTabOverridePlugin} from './editor/plugins/ListTabOverridePlugin';
import {LockedTableCellsPlugin} from './editor/plugins/LockedTableCellsPlugin';
import {MediaFeaturesProvider} from './editor/plugins/MediaFeaturesContext';
import {MediaUploadProvider, type RichTextMediaUpload} from './editor/plugins/MediaUploadContext';
import {MentionTypeaheadOption, NewMentionsPlugin} from './editor/plugins/MentionPlugin';
import {
  addPasteErrorDiagnostics,
  PasteDiagnosticsPlugin,
} from './editor/plugins/PasteDiagnosticsPlugin';
import {PasteTrackingPlugin} from './editor/plugins/PasteTrackingPlugin';
import {PlainTextPlugin} from './editor/plugins/PlainTextPlugin';
import {PreventTablePastePlugin} from './editor/plugins/PreventTablePastePlugin';
import {RedactionAccessPlugin} from './editor/plugins/RedactionAccessPlugin';
import {RedactionV2Provider} from './editor/plugins/redactionV2/RedactionV2Context';
import {
  FloatingRedactionV2EditorPlugin,
  RedactionV2GuardsPlugin,
} from './editor/plugins/redactionV2/RedactionV2Plugin';
import {RichPastePlugin} from './editor/plugins/RichPastePlugin';
import {RichTextToolbar} from './editor/plugins/RichTextToolbarPlugin';
import {SanitizeCommentPastePlugin} from './editor/plugins/SanitizeCommentPastePlugin';
import {SelectedTextControls} from './editor/plugins/SelectedTextToolbar';
import {SelectionPreservationPlugin} from './editor/plugins/SelectionPreservationPlugin';
import {ShortcutsPlugin} from './editor/plugins/ShortcutsPlugin';
import {StripPaperProfileLinksPlugin} from './editor/plugins/StripPaperProfileLinksPlugin';
import {StripUnknownCommentThreadsPlugin} from './editor/plugins/StripUnknownCommentThreadsPlugin';
import TableAddRowColumnPlugin from './editor/plugins/tableButtons/TableAddRowColumnPlugin';
import TableCellResizerPlugin from './editor/plugins/TableCellResizer';
import {TableOfContentsPlugin} from './editor/plugins/TableOfContentsPlugin';
import {TextSelectionPlugin} from './editor/plugins/TextSelectionPlugin';
import {VideosPlugin} from './editor/plugins/VideosPlugin';
import {VimeoPlugin} from './editor/plugins/VimeoPlugin';
import {YouTubePlugin} from './editor/plugins/YouTubePlugin';
import getEditorTheme, {
  type EditorProfile,
  type EditorTextColor,
  type EditorTextSize,
} from './editor/themes/EditorTheme';
import {
  TRANSFORMERS,
  TRANSFORMERS_WITH_ENHANCED_HEADINGS,
  TRANSFORMERS_WITHOUT_HEADINGS,
} from './editor/transformers';
import {
  clearYDocFromCache,
  CollabUser,
  createWebsocketProvider,
  getCollabDocumentName,
} from './editor/utils/collabProvider';
import {decodeYjsAndInsertContent} from './editor/utils/yjsDecoder';

const config = {
  namespace: 'Editor',
  onError(error: Error, editor: LexicalEditor) {
    addPasteErrorDiagnostics(error, editor);
    throw error;
  },
  editorState: null,
  nodes: editorNodes,
};

/** Readable line length for descriptions/notes (matches workstream details description). */
const RICH_TEXT_READABLE_MAX_WIDTH = 720;

const resolveRichTextMaxWidth = (
  maxWidth?: number | string | boolean
): number | string | undefined => {
  if (maxWidth === true) {
    return RICH_TEXT_READABLE_MAX_WIDTH;
  }
  if (maxWidth === false || maxWidth == null) {
    return undefined;
  }
  return maxWidth;
};

export interface Props {
  links?: boolean;
  toc?: boolean;
  clickLinks?: boolean;
  openExternalLinksInNewTab?: boolean;
  enhancedHeadings?: EnhancedHeadingType;
  enhancedTableCells?: boolean;
  source?: string;
  planningId?: string;
  planningTableRightReservePx?: number;
  commentsDocumentId?: string;
  collabId?: string;
  collabOwner?: EmployeeLdap;
  truncateLines?: number;
  /** When set with read-only line clamp, reports whether content exceeds the clamp (scrollHeight > client height). */
  onTruncateOverflowChange?: (isTruncated: boolean) => void;
  hideToolbar?: boolean;
  textSize?: EditorTextSize;
  textColor?: EditorTextColor;
  textBold?: boolean;
  profile?: EditorProfile; // Note: setting a profile overwrites text preferences
  autoFocus?: boolean;
  placeholder?: ReactNode;
  placeholderOffsetX?: number | string;
  topOffset?: number;
  selectionControls?: SelectedTextControls[];
  /** Renders persisted AI suggestion nodes as plain text and disables their click handling. */
  disableAISuggestions?: boolean;
  alwaysShowSelectionControls?: boolean;
  suppressActiveCommentDisplay?: boolean;
  media?: boolean;
  showHeadingControl?: boolean;
  table?: boolean;
  minHeight?: number | string;
  contentEditableHeight?: number | string;
  maxHeight?: number | string;
  /** Pass `true` (or bare `maxWidth`) for the default readable width (720px), or a custom width. */
  maxWidth?: number | string | boolean;
  mentions?: boolean | MentionTypeaheadOption[];
  /** People-only @mention typeahead that never notifies; works with `plainText` too. */
  peopleMentions?: boolean;
  disableStickyToolbar?: boolean;
  editable?: boolean;
  /** Keeps the editor UI visible while preventing edits and showing disabled styling. */
  disabled?: boolean;
  expanded?: boolean;
  defaultText?: ReactNode;
  editablePaddingX?: string;
  editablePaddingY?: string;
  paddingX?: string;
  contentClassName?: string;
  backgroundHighlight?: Atoms['backgroundColor'];
  squareBorders?: boolean;
  selectAll?: boolean;
  value?: string;
  yjsValue?: Uint8Array;
  not100?: boolean;
  disableBorder?: boolean;
  activeThreadId?: string;
  hoveredThreadId?: string;
  commentMentions?: MentionTypeaheadOption[];
  autoMentionTag?: MentionTypeaheadOption[];
  stripUnknownCommentThreads?: boolean;
  validCommentThreadIds?: string[];
  hideCommentHighlights?: boolean;
  onCollabConnected?: (connected: boolean) => void;
  onDocumentSynced?: (synced: boolean) => void;
  onCollabUsersChange?: (users: CollabUser[]) => void;
  editorRole?: 'template' | 'document';
  onSectionChanged?: (headingKey: string, text: string, settings: any) => void;

  onChange?: (state: EditorState) => void;
  onReady?: (editorNodes: ChildNode[]) => void;
  onEditorReady?: (editor: LexicalEditor) => void;
  onToggleFocused?: (focused: boolean) => void;
  onDocumentReady?: (editor: LexicalEditor) => void;
  /** With `onDocumentReady`: `collab` waits for stable HTML; `static` is for fixed Yjs blobs (e.g. version diff). */
  documentExportPreset?: DocumentExportPreset;
  /** When true, Tab moves focus to the next field only when the caret is at the end of a non-empty block (otherwise Tab indents as usual). */
  focusNextOnTabAtBlockEnd?: boolean;
  /** Shift+Enter and Option/Alt+Enter insert a newline; plain Enter calls this callback. */
  onEnterSubmit?: () => void;
  disableTables?: boolean;
  disableTableSelection?: boolean;
  plainText?: boolean;
  sanitizeCommentPaste?: boolean;
  /** Adds privacy-safe Sentry breadcrumbs for diagnosing paste selection failures. */
  pasteDiagnostics?: boolean;
  trackPaste?: boolean;
  onPaste?: () => void;
  onKeystroke?: () => void;
  allowImages?: boolean;
  mediaUpload?: RichTextMediaUpload;
  /** With `media`: allow uploading MP4/MOV/WebM videos (with generated poster thumbnails). */
  videoUploads?: boolean;
  /** With `media`: show full-width / wrap-left / wrap-right controls on selected images. */
  imageLayout?: boolean;
  /**
   * Keep document structure on HTML paste (headings, task lists, quotes, highlights, floated
   * images, Aura embeds), including Dropbox Paper's clipboard format. For long-form editors.
   */
  richPaste?: boolean;
  importHtmlH3H4?: boolean;
}

function warnIfPaperWithTypographySize(props: Props) {
  if (isProd) {
    return;
  }
  if (props.profile !== 'paper') {
    return;
  }
  if (!Object.prototype.hasOwnProperty.call(props, 'textSize')) {
    return;
  }
  // eslint-disable-next-line no-console -- dev-only guardrail for misleading prop combinations
  console.warn(
    '[RichTextArea] `profile="paper"` uses a fixed document body and heading scale, so `textSize` has no effect. Remove `textSize`, or use `profile="default"` if you need large/medium/small sizing. `textColor` and `textBold` still apply in paper mode.'
  );
}

const detectAndInsertContent = (
  editor: LexicalEditor,
  content: string,
  onReady?: (editorNodes: ChildNode[]) => void,
  source?: string,
  selectAll?: boolean,
  markdownTransformers: Transformer[] = TRANSFORMERS,
  importHtmlH3H4 = false
) => {
  let jsonParseFailed = false;

  const handleReady = () => {
    onReady?.([...(editor.getRootElement()?.childNodes ?? [])]);
    if (selectAll) {
      editor.update(() => {
        $selectAll();
      });
    }
  };

  // Try JSON first (most specific)
  if (content.trim().startsWith('{') || content.trim().startsWith('[')) {
    try {
      const json = JSON.parse(content);
      editor.update(() => {
        editor.setEditorState(editor.parseEditorState(json));
        handleReady();
      });
      return;
    } catch {
      jsonParseFailed = true;
      // Not valid JSON, continue to other formats
    }
  }

  // Check for HTML tags
  if (content.includes('<') && content.includes('>')) {
    try {
      const parser = new DOMParser();
      const dom = parser.parseFromString(content, 'text/html');
      // Get the body content since parseFromString creates a full HTML document
      const bodyElement = dom.body;
      if (bodyElement && bodyElement.children.length > 0) {
        if (importHtmlH3H4) {
          bodyElement
            .querySelectorAll('h3, h4')
            .forEach((element) => element.setAttribute(SCOPED_HTML_HEADING_IMPORT_ATTR, 'true'));
        }
        // Convert body children to Lexical nodes
        editor.update(() => {
          const nodes = $generateNodesFromDOM(editor as any, dom);
          const root = $getRoot();
          root.clear();
          // Only ElementNode and DecoratorNode can be direct children of root
          // Wrap inline nodes (like TextNode) in a paragraph to avoid Lexical error #282
          nodes.forEach((node) => {
            if ($isElementNode(node) || $isDecoratorNode(node)) {
              root.append(node);
            } else {
              const paragraph = $createParagraphNode();
              paragraph.append(node);
              root.append(paragraph);
            }
          });
          handleReady();
        });
      } else {
        // Fallback if no body element found
        editor.update(() => {
          const root = $getRoot();
          root.clear();
          const paragraphNode = $createParagraphNode();
          paragraphNode.append($createTextNode(content));
          root.append(paragraphNode);
          handleReady();
        });
      }

      return;
    } catch (e) {
      // HTML parsing failed, continue
      console.error('HTML parsing failed', e);
    }
  }

  // Content looked like JSON (leading `{`/`[`) but didn't parse: record a
  // diagnostic before we render it as Markdown/plain text, so corrupted Lexical
  // state is still surfaced even though we now recover gracefully below.
  if (source && jsonParseFailed) {
    Sentry.captureException(new Error(`[RichTextEditor] Invalid JSON in ${source}`));
  }

  // Anything that isn't Lexical JSON or HTML is run through the real Lexical
  // Markdown importer. We intentionally do NOT gate this on a "looks like
  // markdown" heuristic: that heuristic silently dropped ordered lists,
  // italics, and multi-line prose to the raw-text fallback below (rendering
  // literal `**`/`- `/etc.), and it's the reason Markdown-authored content
  // (e.g. MCP-posted status updates stored as text) showed up unrendered.
  // $convertFromMarkdownString renders plain prose as plain paragraphs, so
  // running it unconditionally is strictly more reliable; the plain-text
  // fallback only kicks in if the importer throws.
  try {
    editor.update(() => {
      $convertFromMarkdownString(content, markdownTransformers);
      handleReady();
    });
    return;
  } catch {
    // Markdown import failed; fall back to raw text below.
  }

  // Fallback to plain text
  editor.update(() => {
    const root = $getRoot();
    root.clear();
    const paragraphNode = $createParagraphNode();
    paragraphNode.append($createTextNode(content));
    root.append(paragraphNode);
    handleReady();
  });
};

// eslint-disable-next-line react/display-name
const EditorContent = forwardRef(
  (
    {
      source,
      placeholder,
      placeholderOffsetX = 10,
      autoFocus = true,
      toc,
      links = true,
      collabId,
      collabOwner,
      clickLinks = false,
      openExternalLinksInNewTab = false,
      squareBorders,
      editable,
      disabled = false,
      expanded,
      hideToolbar,
      selectionControls,
      alwaysShowSelectionControls,
      suppressActiveCommentDisplay,
      activeThreadId,
      minHeight = 150,
      contentEditableHeight = undefined,
      disableStickyToolbar,
      editablePaddingX = '10',
      editablePaddingY = '0',
      paddingX = undefined,
      contentClassName = undefined,
      defaultText = null,
      disableBorder = false,
      media,
      showHeadingControl,
      truncateLines = undefined,
      topOffset = 48,
      onTruncateOverflowChange,
      mentions,
      peopleMentions,
      disableAISuggestions,
      selectAll,
      backgroundHighlight,
      commentMentions,
      autoMentionTag,
      value,
      yjsValue,
      onChange,
      onReady,
      onEditorReady,
      onDocumentReady,
      documentExportPreset = 'collab',
      onToggleFocused,
      onCollabConnected,
      onDocumentSynced,
      onCollabUsersChange,
      not100,
      maxHeight,
      maxWidth,
      enhancedHeadings,
      enhancedTableCells,
      disableTables,
      disableTableSelection,
      // onSectionChanged,
      stripUnknownCommentThreads,
      validCommentThreadIds,
      hideCommentHighlights,
      hoveredThreadId,
      plainText,
      sanitizeCommentPaste,
      pasteDiagnostics,
      trackPaste,
      onPaste,
      onKeystroke,
      focusNextOnTabAtBlockEnd,
      onEnterSubmit,
      allowImages,
      importHtmlH3H4,
      table,
      richPaste,
    }: Props,
    ref
  ) => {
    const {token} = useCollabJWTToken();
    const isTemplateMode = useIsTemplateMode();
    const markdownTransformers =
      isTemplateMode && enhancedHeadings === 'enabled'
        ? TRANSFORMERS_WITH_ENHANCED_HEADINGS
        : TRANSFORMERS;
    const employee = useAtomValue(employeeAtom);
    const color = avatarColorPalette(nameToInitials(employee?.name ?? ''));
    const cursorsContainerRef = useRef<HTMLDivElement>(null);
    const lineClampMeasureRef = useRef<HTMLDivElement | null>(null);
    const [editor] = useLexicalComposerContext();

    const [documentSynced, setDocumentSynced] = useState(false);
    const imagesAllowed = allowImages ?? source !== 'comments';
    const displayValue = imagesAllowed ? value : sanitizeCommentContent(value);
    const isEditorInteractive = Boolean(editable) && !disabled;
    const isEditorDisabled = Boolean(editable) && !isEditorInteractive;

    useEffect(() => {
      onEditorReady?.(editor);
    }, [editor, onEditorReady]);

    useImperativeHandle(ref, () => ({
      focusEditor: () => editor.focus(),
      getEditorState: () => editor.getEditorState(),
      setEditorValue: () => {
        editor.update(() => {
          $getRoot().clear();
        });
      },
      clearEditor: (resetValue?: string) => {
        editor.update(() => {
          if (!resetValue) {
            $getRoot().clear();
          } else {
            const nextValue = imagesAllowed ? resetValue : sanitizeCommentContent(resetValue);
            const root = $getRoot();
            root.clear();

            try {
              const json = JSON.parse(nextValue);
              editor.setEditorState(editor.parseEditorState(json));
            } catch (err) {
              root.clear();
              let parsedLexicalShape: Record<string, unknown> | null = null;
              try {
                const o = JSON.parse(nextValue) as unknown;
                if (o && typeof o === 'object' && o !== null && 'root' in o) {
                  parsedLexicalShape = o as Record<string, unknown>;
                }
              } catch {
                /* fall through to plaintext fallback */
              }
              if (parsedLexicalShape) {
                if (source) {
                  Sentry.captureException(
                    err instanceof Error
                      ? err
                      : new Error('[RichTextEditor] parseEditorState failed'),
                    {extra: {source}}
                  );
                }
                return;
              }

              const paragraphNode = $createParagraphNode();
              paragraphNode.append($createTextNode(nextValue));
              root.append(paragraphNode);

              if (source) {
                Sentry.captureException(new Error(`[RichTextEditor] Invalid JSON in ${source}`));
              }
            }
          }
        });
      },

      setContentWithDetectedFormat: (content: string, options?: {selectAll?: boolean}) => {
        const nextContent = imagesAllowed ? content : sanitizeCommentContent(content);
        detectAndInsertContent(
          editor,
          nextContent,
          onReady,
          source,
          options?.selectAll ?? selectAll,
          markdownTransformers,
          importHtmlH3H4
        );
      },
      parseEditorState: (state: string) =>
        editor.parseEditorState(imagesAllowed ? state : sanitizeCommentContent(state)),
    }));

    useEffect(() => {
      editor.setEditable(isEditorInteractive);
    }, [editor, isEditorInteractive]);

    // Clear YDoc cache on unmount to ensure fresh state on next mount
    useEffect(() => {
      // Skip cleanup in development to avoid destroying the Y.Doc during the first mount cycle
      if (import.meta.env.DEV) {
        return;
      }

      return () => {
        if (collabId) {
          clearYDocFromCache(getCollabDocumentName(collabId));
        }
      };
    }, [collabId]);

    const lastLoadedValueRef = useRef<string | Uint8Array | null>(null);
    const pendingExternalSyncRef = useRef<string | Uint8Array | null>(null);

    const isEditorContentFocused = useCallback((): boolean => {
      const editorRoot = editor.getRootElement();
      if (!editorRoot) {
        return false;
      }
      const active = document.activeElement;
      return active !== null && (editorRoot === active || editorRoot.contains(active));
    }, [editor]);

    const applyExternalEditorValue = useCallback(
      (currentValue: string | Uint8Array | null) => {
        lastLoadedValueRef.current = currentValue;
        pendingExternalSyncRef.current = null;
        if (yjsValue) {
          decodeYjsAndInsertContent(editor, yjsValue, onReady, source);
        } else if (displayValue) {
          detectAndInsertContent(
            editor,
            displayValue,
            onReady,
            source,
            selectAll,
            markdownTransformers,
            importHtmlH3H4
          );
        }
      },
      [
        displayValue,
        editor,
        importHtmlH3H4,
        markdownTransformers,
        onReady,
        selectAll,
        source,
        yjsValue,
      ]
    );

    useEffect(() => {
      if (collabId) {
        return;
      }

      const currentValue = yjsValue ?? displayValue ?? null;
      const isFirstLoad = lastLoadedValueRef.current === null;

      // Skip if loaded
      if (lastLoadedValueRef.current === currentValue) {
        return;
      }

      // Remote updates (e.g. Yjs updating a nested editor in another tab) must apply even when
      // `editable` is true. Only defer while this editor is focused or a comment thread is active.
      if (!isFirstLoad && (isEditorContentFocused() || activeThreadId)) {
        pendingExternalSyncRef.current = currentValue;
        return;
      }

      applyExternalEditorValue(currentValue);
    }, [
      activeThreadId,
      applyExternalEditorValue,
      collabId,
      displayValue,
      editor,
      isEditorContentFocused,
      markdownTransformers,
      onReady,
      selectAll,
      source,
      yjsValue,
    ]);

    const handleContentEditableBlur = useCallback(() => {
      onToggleFocused?.(false);
      if (collabId) {
        return;
      }
      const pending = pendingExternalSyncRef.current;
      if (pending === null) {
        return;
      }
      const currentValue = yjsValue ?? displayValue ?? null;
      if (currentValue !== pending) {
        pendingExternalSyncRef.current = null;
        return;
      }
      if (isEditorContentFocused() || activeThreadId) {
        return;
      }
      applyExternalEditorValue(currentValue);
    }, [
      activeThreadId,
      applyExternalEditorValue,
      collabId,
      displayValue,
      isEditorContentFocused,
      onToggleFocused,
      yjsValue,
    ]);

    // Trigger onChange after collab document is synced
    useEffect(() => {
      if (documentSynced && onChange && collabId) {
        onChange(editor.getEditorState());
      }
    }, [documentSynced, onChange, editor, collabId]);

    useEffect(() => {
      onDocumentSynced?.(collabId ? documentSynced : true);
    }, [collabId, documentSynced, onDocumentSynced]);

    const usesWebkitLineClamp =
      Boolean(truncateLines && truncateLines > 0) &&
      !enhancedTableCells &&
      !editable &&
      !(expanded === false && maxHeight);

    const shouldMeasureLineClampOverflow = Boolean(onTruncateOverflowChange && usesWebkitLineClamp);

    useLineClampOverflowMeasure({
      containerRef: lineClampMeasureRef,
      editor,
      measureEnabled: shouldMeasureLineClampOverflow,
      onOverflowChange: onTruncateOverflowChange,
      displayValue,
      documentSynced,
      yjsValue,
      truncateLines,
      enhancedTableCells,
      editable,
      expanded,
      maxHeight,
    });

    if (!editable && !displayValue && !defaultText && !yjsValue) {
      return null;
    }

    if (collabId && !token && !employee?.name?.length) {
      return null;
    }

    const handleCollabSync = () => {
      if ((displayValue || yjsValue) && collabId) {
        setDocumentSynced(true);
        editor.update(() => {
          const root = $getRoot();
          const hasContent = root.getTextContent().trim().length > 0;

          if (!hasContent) {
            if (yjsValue) {
              decodeYjsAndInsertContent(editor, yjsValue, onReady, source);
            } else if (displayValue) {
              detectAndInsertContent(
                editor,
                displayValue,
                onReady,
                source,
                undefined,
                markdownTransformers,
                importHtmlH3H4
              );
            }
          }
        });
      }
    };

    const resolvedMaxWidth = resolveRichTextMaxWidth(maxWidth);

    return (
      <Box
        ref={shouldMeasureLineClampOverflow ? lineClampMeasureRef : undefined}
        borderWidth="1"
        borderStyle={editable && !disableBorder ? 'Solid' : undefined}
        borderRadius={squareBorders ? undefined : 'Small'}
        borderColor="Border Subtle"
        backgroundColor={editable && backgroundHighlight ? backgroundHighlight : undefined}
        display="block"
        height={!not100 ? '100%' : undefined}
        paddingY={editablePaddingY as any}
        style={{
          ...(resolvedMaxWidth != null ? {maxWidth: resolvedMaxWidth, minWidth: 0} : {}),
          ...(enhancedTableCells || editable || (expanded === false && maxHeight)
            ? {maxHeight, overflow: maxHeight ? 'auto' : undefined}
            : {
                display: '-webkit-box',
                WebkitLineClamp: truncateLines,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }),
        }}
      >
        {editable && !hideToolbar && (
          <RichTextToolbar
            top={topOffset}
            media={Boolean(media)}
            table={Boolean(table) && !plainText && !disableTables}
            disabled={disabled}
            disableStickyToolbar={disableStickyToolbar}
            showHeadingControl={showHeadingControl}
          />
        )}

        <Box
          position="relative"
          borderRadius="Small"
          style={{height: `calc(100% - ${hideToolbar ? 0 : 48}px)`}}
        >
          <RichTextPlugin
            contentEditable={
              <Box paddingX={editable ? (editablePaddingX as any) : paddingX} height="100%">
                <ContentEditable
                  spellCheck={enhancedHeadings !== 'enabled'}
                  onFocus={() => onToggleFocused?.(true)}
                  onBlur={handleContentEditableBlur}
                  className={cx(styles.content, contentClassName, {
                    [styles.input]: editable,
                    [styles.disabledInput]: isEditorDisabled,
                    [aiSuggestionStyles['disabled-suggestions']]: disableAISuggestions,
                  })}
                  style={
                    editable
                      ? contentEditableHeight != null
                        ? {height: contentEditableHeight, overflowY: 'auto'}
                        : {minHeight, height: '100%'}
                      : undefined
                  }
                />
              </Box>
            }
            placeholder={
              collabId && !documentSynced ? (
                <PageSpinner />
              ) : editable ? (
                <Text className={styles.placeholder} style={{left: placeholderOffsetX}}>
                  {placeholder}
                </Text>
              ) : (
                <Box>{defaultText}</Box>
              )
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
          {collabId && editable && (
            <div
              id="asdf"
              ref={cursorsContainerRef}
              style={{
                position: 'absolute',
                top: topOffset + 48,
                left: 0,
                right: 0,
                bottom: 0,
                pointerEvents: 'none',
                zIndex: 10,
              }}
            />
          )}
          {!collabId && <HistoryPlugin />}
          {collabId && token && employee?.name?.length && (
            <>
              <CollaborationPlugin
                id={getCollabDocumentName(collabId)}
                providerFactory={(id, yjsDocMap) =>
                  createWebsocketProvider(
                    token,
                    id,
                    yjsDocMap,
                    () => onCollabConnected?.(true),
                    () => onCollabConnected?.(false),
                    onCollabUsersChange,
                    handleCollabSync,
                    employee
                  ) as any
                }
                shouldBootstrap
                initialEditorState={null}
                cursorsContainerRef={cursorsContainerRef}
                username={employee?.name}
                cursorColor={color.background}
                excludedProperties={editorCollabExcludedProperties}
              />
              <SelectionPreservationPlugin />
            </>
          )}
          <EnhancedHeadingPlugin enhancedHeadings={enhancedHeadings} />
          <EnhancedTableCellPlugin enhancedTableCells={enhancedTableCells} />
          {!plainText && <ListPlugin />}
          {(focusNextOnTabAtBlockEnd || onEnterSubmit) && (
            <FormKeyboardNavigationPlugin
              focusNextOnTabAtBlockEnd={focusNextOnTabAtBlockEnd}
              onEnterSubmit={onEnterSubmit}
            />
          )}
          {!plainText && <ListTabOverridePlugin />}
          {!plainText && <ListBackspacePlugin />}
          {!plainText && <LinkPlugin />}
          {!plainText && <LinkNodePlugin />}
          <LockedTableCellsPlugin />
          <DynamicNodeDeletionGuardPlugin />

          {plainText || disableTables ? (
            <PreventTablePastePlugin />
          ) : (
            <>
              {!editable && disableTableSelection && <DisableTableSelectionPlugin />}

              <TablePlugin hasHorizontalScroll={true} />
              <NormalizeTableCellsPlugin />
              <TableCellResizerPlugin />
              {editable && <TableAddRowColumnPlugin />}
            </>
          )}

          {!plainText && <AutoLinkPlugin />}
          {!plainText && <AutoLinkCompanyOSPlugin />}
          {editable && !plainText && richPaste && <RichPastePlugin />}
          <EmptyLinePastePlugin />
          <StripPaperProfileLinksPlugin />
          <EllipsisReplacementPlugin />
          {!plainText && <ShortcutsPlugin />}
          {pasteDiagnostics && <PasteDiagnosticsPlugin source={source} />}
          {trackPaste && <PasteTrackingPlugin source={source} onPaste={onPaste} />}
          {onKeystroke && <KeystrokeCountPlugin onKeystroke={onKeystroke} />}
          {plainText && <PlainTextPlugin />}
          {sanitizeCommentPaste && <SanitizeCommentPastePlugin />}
          {selectionControls?.length && (
            <TextSelectionPlugin
              controls={selectionControls}
              alwaysShow={alwaysShowSelectionControls}
              documentSynced={collabId ? documentSynced : true}
              hideCommentHighlights={hideCommentHighlights}
              hoveredThreadId={hoveredThreadId}
              commentMentions={source === 'planning-doc' ? commentMentions : undefined}
              suppressActiveCommentDisplay={suppressActiveCommentDisplay}
              disableAISuggestions={disableAISuggestions}
            />
          )}
          {stripUnknownCommentThreads && (
            <StripUnknownCommentThreadsPlugin allowedThreadIds={validCommentThreadIds ?? []} />
          )}
          {peopleMentions ? (
            <NewMentionsPlugin mentions={[]} peopleOnly />
          ) : (
            !plainText &&
            mentions && (
              <NewMentionsPlugin
                autoMentionTag={autoMentionTag}
                mentions={typeof mentions === 'boolean' ? (commentMentions ?? []) : mentions}
                documentSynced={collabId ? documentSynced : undefined}
              />
            )
          )}
          {!plainText && <EmojiPickerPlugin />}
          {!imagesAllowed && <DisallowImagesPlugin />}

          {!plainText && media && (
            <>
              <AutoEmbedPlugin />
              <YouTubePlugin />
              <CapturePlugin />
              <AuraVideoPlugin />
              <VimeoPlugin />
              <KalturaPlugin />
              <ImagesPlugin />
              <VideosPlugin />
              <DragDropPastePlugin />
            </>
          )}
          <ImageLightboxPlugin />
          {!plainText && <TabIndentationPlugin />}
          {!plainText && <CheckListPlugin />}
          {!plainText && <RedactionAccessPlugin />}
          {!plainText && collabId && <RedactionV2GuardsPlugin />}
          {collabOwner && <FloatingRedactionEditorPlugin collabOwner={collabOwner} />}
          {!plainText && collabId && collabOwner && (
            <FloatingRedactionV2EditorPlugin collabOwner={collabOwner} />
          )}

          {toc && <TableOfContentsPlugin />}
          {autoFocus && <AutoFocusPlugin />}
          {!plainText && links && (
            <>
              <LexicalClickableLinkPlugin
                newTab={false}
                externalLinksInNewTab={openExternalLinksInNewTab}
              />
              {editable && !clickLinks && <FloatingLinkEditorPlugin />}
            </>
          )}
          <OnChangePlugin
            ignoreSelectionChange
            onChange={(state, editorInstance) => {
              const jsonState = state.toJSON();
              const {children} = jsonState.root;

              // Helper to check if a node is "empty" (no meaningful content)
              const isEmptyNode = (node: any): boolean => {
                if (!node) return true;
                if (node.type === 'linebreak') return true;
                if (node.type === 'text') return !node.text?.trim();
                if (node.children) {
                  return node.children.length === 0 || node.children.every(isEmptyNode);
                }
                return false;
              };

              // Trim empty nodes from both ends of an array
              const trimEmptyNodes = (nodes: any[]): any[] => {
                const firstValid = nodes.findIndex((n) => !isEmptyNode(n));
                if (firstValid === -1) return [];
                // Find last valid by searching from the end
                let lastValid = nodes.length - 1;
                while (lastValid >= firstValid && isEmptyNode(nodes[lastValid])) {
                  lastValid--;
                }
                return nodes.slice(firstValid, lastValid + 1);
              };

              // Trim leading/trailing empty paragraphs from root
              jsonState.root.children = trimEmptyNodes(children);

              // Trim leading/trailing empty nodes within first and last paragraphs
              if (jsonState.root.children.length > 0) {
                const first = jsonState.root.children[0] as any;
                const last = jsonState.root.children[jsonState.root.children.length - 1] as any;

                if (first.children) first.children = trimEmptyNodes(first.children);
                if (last !== first && last.children) last.children = trimEmptyNodes(last.children);
              }

              // Check for meaningful content: text content OR decorator nodes (like emojis)
              const rootElement = editorInstance.getRootElement();
              const hasTextContent = Boolean(rootElement?.textContent?.trim().length);
              const hasDecoratorNodes = Boolean(
                rootElement?.querySelector('[data-lexical-decorator="true"]')
              );
              const hasContent = hasTextContent || hasDecoratorNodes;

              onChange?.(
                Object.assign(state, {
                  toString: () => (hasContent ? JSON.stringify(jsonState) : ''),
                })
              );
            }}
          />
          {onDocumentReady && (
            <DocumentExportPlugin onDocmentReady={onDocumentReady} preset={documentExportPreset} />
          )}
          {!plainText && (
            <MarkdownShortcutPlugin
              transformers={
                isTemplateMode && enhancedHeadings === 'enabled'
                  ? TRANSFORMERS_WITH_ENHANCED_HEADINGS
                  : TRANSFORMERS_WITHOUT_HEADINGS
              }
            />
          )}
        </Box>
      </Box>
    );
  }
);

// eslint-disable-next-line react/display-name
export const RichTextArea = forwardRef((props: Props, ref) => {
  const hasExplicitTextSize = Object.prototype.hasOwnProperty.call(props, 'textSize');
  const isEditorInteractive = Boolean(props.editable) && !props.disabled;

  useEffect(() => {
    warnIfPaperWithTypographySize(props);
    // Intentionally narrow: warn when paper profile is combined with an explicit textSize prop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.profile, hasExplicitTextSize]);

  return (
    <CollabProvider
      collabId={props.collabId}
      planningId={props.planningId}
      commentsDocumentId={props.commentsDocumentId}
      collabOwner={props.collabOwner}
    >
      <RoleProvider role={props.editorRole ?? 'document'} isEditable={isEditorInteractive}>
        <LexicalCollaboration>
          <LexicalComposer
            initialConfig={{
              ...config,
              editable: isEditorInteractive,
              theme: getEditorTheme({
                textSize: props.textSize,
                textColor: props.textColor,
                textBold: props.textBold,
                profile: props.profile,
              }),
            }}
          >
            <PlanningTableReserveProvider value={props.planningTableRightReservePx ?? 0}>
              <RedactionV2Provider
                documentId={props.collabId}
                enabled={Boolean(props.collabId && !props.plainText)}
              >
                <MediaUploadProvider value={props.mediaUpload}>
                  <MediaFeaturesProvider
                    value={{
                      videoUploads: Boolean(props.media && props.videoUploads),
                      imageLayout: Boolean(props.media && props.imageLayout),
                    }}
                  >
                    <EditorContent {...props} ref={ref} />
                  </MediaFeaturesProvider>
                </MediaUploadProvider>
              </RedactionV2Provider>
            </PlanningTableReserveProvider>
          </LexicalComposer>
        </LexicalCollaboration>
      </RoleProvider>
    </CollabProvider>
  );
});
